import { v4 as uuidv4 } from 'uuid';
import uniqBy from 'lodash/uniqBy';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import { DOMElement } from 'slate-react/dist/utils/dom';

const TEMP_ID_PREFIX = 'temp-';

export const removeDupes = <T>(input: T[], key = 'id'): T[] => {
    return uniqBy(input, key);
}

export const cleanObject = (input: any) => {
    return omitBy(input, isUndefined);
}

export const extractId = (input: string | any) => {
    if (input && typeof input === 'object') {
        return input.id;
    }

    return input;
}

export const getCleanIdFromUrl = (input: string, divider = '-') => {
    if (!input) {
        return input;
    }

    return input.split(divider)[0];
}

export const escapeRegExp = (input: string) => input.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

export const convertToFormData = (source: any) => {
    const formData = new FormData();

    const append = (key: string, value: any) => {
        if (value instanceof File || (value && typeof value === 'object' && 'webkitRelativePath' in value && 'name' in value)) {
            formData.append(key, value, value.name);
        } else {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
    }

    Object.keys(source).forEach(key => {
        if (typeof source[key] !== 'undefined') {
            if (Array.isArray(source[key])) {
                source[key].forEach((value: any) => {
                    append(key + '[]', value);
                });
            } else {
                append(key, source[key]);
            }
        }
    });

    return formData;
}

export const getInitials = (input: string) => input.split(' ').slice(0, 3).map(s => (s.length ? s.charAt(0) : '')).join('').toUpperCase();

export const getFirstName = (name = '') => name.split(' ')[0];

export const setEndOfContenteditable = (contentEditableElement: DOMElement, position: number) => {
    const range = document.createRange();//Create a range (a range is a like the selection but invisible)
    if (typeof position !== 'undefined') {
        range.setStart(contentEditableElement, position);
        range.setEnd(contentEditableElement, position);
    } else {
        range.selectNodeContents(contentEditableElement);//Select the entire contents of the element with the range
        range.collapse(false);//collapse the range to the end point. false means collapse to end rather than the start
    }
    const selection = window.getSelection();//get the selection object (allows you to change selection)
    selection?.removeAllRanges();//remove any selections already made
    selection?.addRange(range);//make the range you have just created the visible selection
}

export const setSelection = (start: { node: DOMElement, offset: number }, end: { node: DOMElement, offset: number }) => {
    try {
        const range = document.createRange();
        if (!end) {
            end = start;
        }

        range.setStart(start.node, Math.min(start.offset, start.node?.textContent?.length || Number.POSITIVE_INFINITY));
        range.setEnd(end.node, Math.min(end.offset, end.node?.textContent?.length || Number.POSITIVE_INFINITY));

        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
    } catch (e) { }
}

export const formatCurrency = (value: number, currency = 'USD') => {
    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
    });

    return currencyFormatter.format(parseFloat((value || 0).toString().replace(',', '.')))
};

export const isTempId = (id?: string) => (id || '').indexOf(TEMP_ID_PREFIX) === 0;

export const createTempId = () => `${TEMP_ID_PREFIX}${uuidv4()}`;

export const scrollTo = (scrollElement: DOMElement, position: number) => {
    if (!scrollElement) {
        return;
    }

    // Edge browser polyfill
    if ('scrollTo' in scrollElement && typeof scrollElement.scrollTo === 'function') {
        scrollElement.scrollTo({
            top: position,
            behavior: 'smooth'
        });
    } else {
        scrollElement.scrollTop = position;
    }
}

export const formatPrice = (input: number) => Math.abs(input).toFixed(2);

export const capitalize = (input: string) => {
    if (typeof input !== 'string') return '';
    return input.charAt(0).toUpperCase() + input.slice(1);
}

export const btoa = window.btoa;

/**
 * Convert an image 
 * to a base64 url
 * @param  {String}   url         
 * @param  {Function} callback    
 * @param  {String}   [outputFormat=image/png]           
 */
export const convertImgToBase64URL = (url: string, outputFormat?: string) => {
    return new Promise(res => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function(){
            let canvas: HTMLCanvasElement | null = document.createElement('canvas');
            const ctx = canvas.getContext('2d')
            let dataURL;
            canvas.height = img.height;
            canvas.width = img.width;
            ctx?.drawImage(img, 0, 0);
            dataURL = canvas.toDataURL(outputFormat);
            res(dataURL);
            canvas = null; 
        };

        img.src = url;
    })

};

export const getStandardLanguage = (language = '') => language?.split('-')[0];

export const download = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('target', '_blank');

    link.click();
}

export const delay = (duration: number) => new Promise(resolve => setTimeout(resolve, duration));

export const hasLocalStorage = () => {
    let supports = false;
    try {
        supports = 'localStorage' in window && typeof localStorage === 'object' && localStorage && typeof localStorage.getItem === 'function' && typeof localStorage.setItem === 'function';
    } catch(e) {
        supports = false;
    }

    return supports;
}

export const shuffle = (array: any[]) => {
    let currentIndex = array.length,  randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex != 0) {

        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }

    return array;
}

export const advancedRound = (input: number, rounder: number = 100) => {
    return Math.round(input * rounder) / rounder;
}

export const sumArray = (array: any[], key?: string) => {
    return array.reduce((partialSum, a) => partialSum + (typeof key !== 'undefined' ? a[key] : a), 0);
}
